import { useState, useEffect } from 'react'
import { Card, Button, Table, Badge, Modal, Form, Row, Col, Spinner, Alert, InputGroup } from 'react-bootstrap'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { useAuth } from '../../hooks/useAuth'
import { useSyncedLocalClientId } from '../../hooks/useSyncedLocalClientId'
import api from '../../services/api'
import { formatDateOnlyEsMX, getTodayDateOnlyMx, startOfLocalDayFromApi } from '../../utils/apiDateFormat'
import ConditionBuilder from './ConditionBuilder'

// Helper para obtener nombre del cliente
const getClientName = (client) => {
  if (client.razon_social) return client.razon_social
  if (client.business_name) return client.business_name
  const parts = []
  if (client.nombre) parts.push(client.nombre)
  if (client.apellido_paterno) parts.push(client.apellido_paterno)
  if (parts.length > 0) return parts.join(' ')
  return client.name || 'N/A'
}

const DeductibilityRules = () => {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [selectedRule, setSelectedRule] = useState(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [ruleToDelete, setRuleToDelete] = useState(null)
  
  // Filtros
  const { clientId: filterCliente, setClientId: setFilterCliente, selectDisabled } = useSyncedLocalClientId()
  const [filterResultado, setFilterResultado] = useState('')
  const [filterActivo, setFilterActivo] = useState('all')
  const [searchValue, setSearchValue] = useState('')
  
  // Ordenamiento y paginación
  const [sortField, setSortField] = useState('nombre')
  const [sortDirection, setSortDirection] = useState('asc')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  
  // Selección múltiple
  const [selectedRules, setSelectedRules] = useState([])
  
  // Formulario
  const [formData, setFormData] = useState({
    isGeneral: false,
    cliente: '',
    clientes: [], // Para selección múltiple (array de IDs)
    nombre: '',
    condiciones: { operator: 'AND', conditions: [] },
    resultado: 'deducible',
    porcentaje_deducible: null,
    fecha_aplicacion: getTodayDateOnlyMx(),
    fecha_fin_vigencia: '',
    activo: true
  })
  
  // Obtener reglas de deducibilidad
  const { data: rules, isLoading } = useQuery(
    ['deductibility-rules', filterCliente, filterResultado, filterActivo],
    async () => {
      const params = new URLSearchParams()
      if (filterCliente) params.append('cliente_id', filterCliente)
      if (filterResultado) params.append('resultado', filterResultado)
      if (filterActivo !== 'all') params.append('activo', filterActivo === 'true')
      
      const response = await api.get(`/accounting/reglas-deducibilidad/?${params.toString()}`)
      return response.data.results || response.data || []
    },
    { enabled: !!user }
  )
  
  // Obtener clientes
  const { data: clients } = useQuery(
    'clients-for-deductibility-rules',
    async () => {
      const response = await api.get('/clients/clients/?activo=true')
      return response.data.results || response.data || []
    },
    { enabled: !!user }
  )
  
  // Mutación para crear regla(s) - una o varias según clientes seleccionados
  const createMutation = useMutation(
    async ({ data, clientIds }) => {
      if (data.cliente === null) {
        const response = await api.post('/accounting/reglas-deducibilidad/', data)
        return { count: 1, response: response.data }
      }
      const results = []
      for (const cid of clientIds) {
        const payload = { ...data, cliente: cid }
        const response = await api.post('/accounting/reglas-deducibilidad/', payload)
        results.push(response.data)
      }
      return { count: results.length, response: results[0] }
    },
    {
      onSuccess: (result) => {
        toast.success(result.count > 1 ? `Regla creada para ${result.count} cliente(s)` : 'Regla creada correctamente')
        queryClient.invalidateQueries('deductibility-rules')
        handleCloseForm()
      },
      onError: (error) => {
        console.error('Error al crear regla:', error.response?.data)
        let errorMessage = 'Error al crear la regla'
        
        if (error.response?.data) {
          // Si hay errores específicos de campo
          const fieldErrors = error.response.data
          const errorMessages = []
          
          Object.keys(fieldErrors).forEach(field => {
            if (Array.isArray(fieldErrors[field])) {
              errorMessages.push(`${field}: ${fieldErrors[field].join(', ')}`)
            } else if (typeof fieldErrors[field] === 'object') {
              Object.keys(fieldErrors[field]).forEach(key => {
                errorMessages.push(`${field}.${key}: ${fieldErrors[field][key].join(', ')}`)
              })
            } else {
              errorMessages.push(`${field}: ${fieldErrors[field]}`)
            }
          })
          
          if (errorMessages.length > 0) {
            errorMessage = errorMessages.join('\n')
          } else if (error.response.data.error) {
            errorMessage = error.response.data.error
          } else if (error.response.data.detail) {
            errorMessage = error.response.data.detail
          }
        }
        
        toast.error(errorMessage, { autoClose: 10000 })
      }
    }
  )
  
  // Mutación para actualizar regla
  const updateMutation = useMutation(
    async ({ id, data }) => {
      const response = await api.put(`/accounting/reglas-deducibilidad/${id}/`, data)
      return response.data
    },
    {
      onSuccess: () => {
        toast.success('Regla actualizada correctamente')
        queryClient.invalidateQueries('deductibility-rules')
        handleCloseForm()
      },
      onError: (error) => {
        console.error('Error al actualizar regla:', error.response?.data)
        let errorMessage = 'Error al actualizar la regla'
        
        if (error.response?.data) {
          // Si hay errores específicos de campo
          const fieldErrors = error.response.data
          const errorMessages = []
          
          Object.keys(fieldErrors).forEach(field => {
            if (Array.isArray(fieldErrors[field])) {
              errorMessages.push(`${field}: ${fieldErrors[field].join(', ')}`)
            } else if (typeof fieldErrors[field] === 'object') {
              Object.keys(fieldErrors[field]).forEach(key => {
                errorMessages.push(`${field}.${key}: ${fieldErrors[field][key].join(', ')}`)
              })
            } else {
              errorMessages.push(`${field}: ${fieldErrors[field]}`)
            }
          })
          
          if (errorMessages.length > 0) {
            errorMessage = errorMessages.join('\n')
          } else if (error.response.data.error) {
            errorMessage = error.response.data.error
          } else if (error.response.data.detail) {
            errorMessage = error.response.data.detail
          }
        }
        
        toast.error(errorMessage, { autoClose: 10000 })
      }
    }
  )
  
  // Mutación para eliminar regla
  const deleteMutation = useMutation(
    async (id) => {
      await api.delete(`/accounting/reglas-deducibilidad/${id}/`)
    },
    {
      onSuccess: () => {
        toast.success('Regla eliminada correctamente')
        queryClient.invalidateQueries('deductibility-rules')
        setShowDeleteConfirm(false)
        setRuleToDelete(null)
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.error || error.response?.data?.detail || 'Error al eliminar la regla'
        toast.error(errorMessage)
      }
    }
  )
  
  const handleOpenForm = (rule = null) => {
    if (rule) {
      setSelectedRule(rule)
      const isGeneral = rule.cliente === null || rule.cliente === undefined
      setFormData({
        isGeneral,
        cliente: rule.cliente || '',
        clientes: rule.cliente ? [rule.cliente] : [],
        nombre: rule.nombre,
        condiciones: rule.condiciones || { operator: 'AND', conditions: [] },
        resultado: rule.resultado,
        porcentaje_deducible: rule.porcentaje_deducible,
        fecha_aplicacion: rule.fecha_aplicacion ? rule.fecha_aplicacion.split('T')[0] : getTodayDateOnlyMx(),
        fecha_fin_vigencia: rule.fecha_fin_vigencia ? rule.fecha_fin_vigencia.split('T')[0] : '',
        activo: rule.activo !== false
      })
    } else {
      setSelectedRule(null)
      setFormData({
        isGeneral: false,
        cliente: '',
        clientes: [],
        nombre: '',
        condiciones: { operator: 'AND', conditions: [] },
        resultado: 'deducible',
        porcentaje_deducible: null,
        fecha_aplicacion: getTodayDateOnlyMx(),
        fecha_fin_vigencia: '',
        activo: true
      })
    }
    setShowForm(true)
  }
  
  const handleCloseForm = () => {
    setShowForm(false)
    setSelectedRule(null)
  }
  
  const handleSubmit = (e) => {
    e.preventDefault()
    
    // Validaciones: General o al menos un cliente
    if (!formData.isGeneral) {
      const clientesIds = Array.isArray(formData.clientes) ? formData.clientes : (formData.cliente ? [formData.cliente] : [])
      if (clientesIds.length === 0) {
        toast.error('Debes seleccionar al menos un cliente o marcar "General"')
        return
      }
    }
    if (!formData.nombre.trim()) {
      toast.error('Debes ingresar un nombre para la regla')
      return
    }
    if (formData.resultado === 'parcial' && (!formData.porcentaje_deducible || formData.porcentaje_deducible <= 0 || formData.porcentaje_deducible > 100)) {
      toast.error('Debes ingresar un porcentaje deducible válido (1-100) cuando el resultado es "Parcial"')
      return
    }
    
    // Preparar datos base (sin cliente)
    const baseData = {
      nombre: formData.nombre.trim(),
      condiciones: formData.condiciones || { operator: 'AND', conditions: [] },
      resultado: formData.resultado,
      porcentaje_deducible: formData.resultado === 'parcial' ? formData.porcentaje_deducible : null,
      fecha_aplicacion: formData.fecha_aplicacion,
      fecha_fin_vigencia: formData.fecha_fin_vigencia || null,
      activo: formData.activo !== false
    }
    
    // Limpiar condiciones vacías o inválidas
    if (baseData.condiciones) {
      // Filtrar condiciones simples vacías
      if (Array.isArray(baseData.condiciones.conditions)) {
        baseData.condiciones.conditions = baseData.condiciones.conditions.filter(cond => {
          if (!cond || typeof cond !== 'object') return false
          const hasField = cond.field && cond.field !== ''
          const hasOperator = cond.operator && cond.operator !== ''
          const hasValue = cond.value !== undefined && cond.value !== null && cond.value !== ''
          const hasValidList = !(Array.isArray(cond.value) && cond.value.length === 0)
          return hasField && hasOperator && hasValue && hasValidList
        })
      }
      
      // Si no hay condiciones válidas ni grupos, enviar objeto vacío
      const hasValidConditions = baseData.condiciones.conditions && baseData.condiciones.conditions.length > 0
      const hasValidGroups = baseData.condiciones.groups && Array.isArray(baseData.condiciones.groups) && baseData.condiciones.groups.length > 0
      
      if (!hasValidConditions && !hasValidGroups) {
        baseData.condiciones = {}
      } else {
        baseData.condiciones = {
          operator: baseData.condiciones.operator || 'AND',
          conditions: baseData.condiciones.conditions || [],
          groups: baseData.condiciones.groups || []
        }
      }
    }
    
    if (selectedRule) {
      const dataToSend = { ...baseData, cliente: formData.isGeneral ? null : (formData.cliente || formData.clientes?.[0]) }
      updateMutation.mutate({ id: selectedRule.id, data: dataToSend })
    } else {
      if (formData.isGeneral) {
        createMutation.mutate({ data: { ...baseData, cliente: null }, clientIds: [null] })
      } else {
        const clientesIds = Array.isArray(formData.clientes) && formData.clientes.length > 0
          ? formData.clientes
          : (formData.cliente ? [formData.cliente] : [])
        createMutation.mutate({ data: baseData, clientIds: clientesIds })
      }
    }
  }
  
  const handleDelete = (rule) => {
    setRuleToDelete(rule)
    setShowDeleteConfirm(true)
  }
  
  const confirmDelete = () => {
    if (ruleToDelete) {
      deleteMutation.mutate(ruleToDelete.id)
    }
  }
  
  // Manejar cambios en condiciones desde ConditionBuilder
  const handleConditionsChange = (newConditions) => {
    setFormData({
      ...formData,
      condiciones: newConditions
    })
  }
  
  // Filtrar reglas por búsqueda
  const filteredRules = rules?.filter(rule => {
    if (searchValue) {
      const searchLower = searchValue.toLowerCase()
      return (
        rule.nombre?.toLowerCase().includes(searchLower) ||
        rule.cliente_name?.toLowerCase().includes(searchLower) ||
        rule.condiciones_legibles?.toLowerCase().includes(searchLower)
      )
    }
    return true
  }) || []
  
  // Función para ordenar
  const sortedRules = [...filteredRules].sort((a, b) => {
    let aValue = a[sortField]
    let bValue = b[sortField]
    
    // Manejar valores nulos
    if (aValue === null || aValue === undefined) aValue = ''
    if (bValue === null || bValue === undefined) bValue = ''
    
    // Convertir a string para comparación
    if (typeof aValue === 'object') {
      aValue = JSON.stringify(aValue)
      bValue = JSON.stringify(bValue)
    } else {
      aValue = String(aValue).toLowerCase()
      bValue = String(bValue).toLowerCase()
    }
    
    if (sortDirection === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0
    }
  })
  
  // Paginación
  const totalPages = Math.ceil(sortedRules.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedRules = sortedRules.slice(startIndex, endIndex)
  
  // Manejar ordenamiento
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }
  
  // Indicador de ordenamiento
  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <i className="bi bi-arrow-down-up text-muted" style={{ fontSize: '0.75rem' }} />
    }
    return sortDirection === 'asc'
      ? <i className="bi bi-arrow-up text-primary" style={{ fontSize: '0.75rem' }} />
      : <i className="bi bi-arrow-down text-primary" style={{ fontSize: '0.75rem' }} />
  }
  
  // Obtener estado de vigencia
  const getVigenciaStatus = (rule) => {
    const today = startOfLocalDayFromApi(getTodayDateOnlyMx())
    const fechaAplicacion = startOfLocalDayFromApi(rule.fecha_aplicacion)
    if (!today || !fechaAplicacion) {
      return { status: 'active', label: 'Activa', badge: 'success' }
    }

    if (fechaAplicacion > today) {
      return { status: 'future', label: 'Futura', badge: 'info', days: Math.ceil((fechaAplicacion - today) / (1000 * 60 * 60 * 24)) }
    }

    if (rule.fecha_fin_vigencia) {
      const fechaFin = startOfLocalDayFromApi(rule.fecha_fin_vigencia)
      if (fechaFin && fechaFin < today) {
        return { status: 'expired', label: 'Vencida', badge: 'secondary', days: Math.ceil((today - fechaFin) / (1000 * 60 * 60 * 24)) }
      }
    }

    return { status: 'active', label: 'Activa', badge: 'success' }
  }
  
  // Selección múltiple
  const toggleSelectRule = (ruleId) => {
    setSelectedRules(prev => 
      prev.includes(ruleId) 
        ? prev.filter(id => id !== ruleId)
        : [...prev, ruleId]
    )
  }
  
  const toggleSelectAll = () => {
    if (selectedRules.length === paginatedRules.length && paginatedRules.length > 0) {
      setSelectedRules([])
    } else {
      setSelectedRules(paginatedRules.map(rule => rule.id))
    }
  }
  
  const clearSelection = () => {
    setSelectedRules([])
  }
  
  // Mutación para activar/desactivar múltiples reglas
  const bulkToggleMutation = useMutation(
    async ({ ids, activo }) => {
      const promises = ids.map(id => 
        api.patch(`/accounting/reglas-deducibilidad/${id}/`, { activo })
      )
      await Promise.all(promises)
    },
    {
      onSuccess: (_, variables) => {
        toast.success(`${variables.ids.length} regla(s) ${variables.activo ? 'activada(s)' : 'desactivada(s)'} correctamente`)
        queryClient.invalidateQueries('deductibility-rules')
        setSelectedRules([])
      },
      onError: (_error) => {
        toast.error('Error al actualizar las reglas')
      }
    }
  )
  
  // Estadísticas
  const stats = {
    total: rules?.length || 0,
    activas: rules?.filter(r => r.activo).length || 0,
    inactivas: rules?.filter(r => !r.activo).length || 0,
    deducibles: rules?.filter(r => r.resultado === 'deducible').length || 0,
    noDeducibles: rules?.filter(r => r.resultado === 'no_deducible').length || 0,
    parciales: rules?.filter(r => r.resultado === 'parcial').length || 0,
    vencidas: rules?.filter(r => {
      if (!r.fecha_fin_vigencia) return false
      const fechaFin = startOfLocalDayFromApi(r.fecha_fin_vigencia)
      const today = startOfLocalDayFromApi(getTodayDateOnlyMx())
      return fechaFin && today && fechaFin < today
    }).length || 0
  }
  
  // Resetear paginación cuando cambian los filtros
  useEffect(() => {
    setCurrentPage(1)
  }, [filterCliente, filterResultado, filterActivo, searchValue])
  
  // Resetear selección cuando cambia la página
  useEffect(() => {
    setSelectedRules([])
  }, [currentPage])
  
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3>Reglas de Deducibilidad</h3>
          <p className="text-muted mb-0">
            <small>Total: {stats.total} | Activas: {stats.activas} | Inactivas: {stats.inactivas} | Vencidas: {stats.vencidas}</small>
          </p>
        </div>
        <Button variant="primary" onClick={() => handleOpenForm()}>
          + Nueva Regla
        </Button>
      </div>
      
      {/* Estadísticas rápidas */}
      <Row className="mb-4">
        <Col md={2}>
          <Card className="text-center border-primary">
            <Card.Body className="py-2">
              <h5 className="mb-0 text-primary">{stats.total}</h5>
              <small className="text-muted">Total</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center border-success">
            <Card.Body className="py-2">
              <h5 className="mb-0 text-success">{stats.activas}</h5>
              <small className="text-muted">Activas</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center border-danger">
            <Card.Body className="py-2">
              <h5 className="mb-0 text-danger">{stats.deducibles}</h5>
              <small className="text-muted">Deducibles</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center border-warning">
            <Card.Body className="py-2">
              <h5 className="mb-0 text-warning">{stats.parciales}</h5>
              <small className="text-muted">Parciales</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center border-secondary">
            <Card.Body className="py-2">
              <h5 className="mb-0 text-secondary">{stats.vencidas}</h5>
              <small className="text-muted">Vencidas</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center">
            <Card.Body className="py-2">
              <h5 className="mb-0">{filteredRules.length}</h5>
              <small className="text-muted">Filtradas</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Filtros */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Cliente</Form.Label>
                <Form.Select
                  value={filterCliente}
                  onChange={(e) => setFilterCliente(e.target.value)}
                  disabled={selectDisabled}
                >
                  <option value="">Todos los clientes</option>
                  <option value="general">General (todas)</option>
                  {clients && clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {getClientName(client)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Resultado</Form.Label>
                <Form.Select
                  value={filterResultado}
                  onChange={(e) => setFilterResultado(e.target.value)}
                >
                  <option value="">Todos</option>
                  <option value="deducible">Deducible</option>
                  <option value="no_deducible">No Deducible</option>
                  <option value="parcial">Parcial</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Estado</Form.Label>
                <Form.Select
                  value={filterActivo}
                  onChange={(e) => setFilterActivo(e.target.value)}
                >
                  <option value="all">Todos</option>
                  <option value="true">Activas</option>
                  <option value="false">Inactivas</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Buscar</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Nombre, cliente o condiciones..."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={2} className="d-flex align-items-end">
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setFilterCliente('')
                  setFilterResultado('')
                  setFilterActivo('all')
                  setSearchValue('')
                }}
                className="w-100"
              >
                Limpiar Filtros
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      
      {/* Acciones masivas */}
      {selectedRules.length > 0 && (
        <Card className="mb-3 border-primary">
          <Card.Body className="py-2">
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-primary">
                <strong>{selectedRules.length}</strong> regla(s) seleccionada(s)
              </span>
              <div className="d-flex gap-2">
                <Button
                  variant="outline-success"
                  size="sm"
                  onClick={() => bulkToggleMutation.mutate({ ids: selectedRules, activo: true })}
                  disabled={bulkToggleMutation.isLoading}
                >
                  Activar
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => bulkToggleMutation.mutate({ ids: selectedRules, activo: false })}
                  disabled={bulkToggleMutation.isLoading}
                >
                  Desactivar
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={clearSelection}
                >
                  Limpiar selección
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}
      
      {/* Tabla de reglas */}
      {isLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando...</span>
          </Spinner>
        </div>
      ) : filteredRules && filteredRules.length > 0 ? (
        <Card>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex align-items-center gap-3">
                <Form.Select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value))
                    setCurrentPage(1)
                  }}
                  style={{ width: 'auto' }}
                  size="sm"
                >
                  <option value={5}>5 por página</option>
                  <option value={10}>10 por página</option>
                  <option value={25}>25 por página</option>
                  <option value={50}>50 por página</option>
                  <option value={100}>100 por página</option>
                </Form.Select>
                <small className="text-muted">
                  Mostrando {startIndex + 1} - {Math.min(endIndex, sortedRules.length)} de {sortedRules.length}
                </small>
              </div>
            </div>
            
            <div className="table-responsive">
              <Table responsive striped hover>
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>
                      <Form.Check
                        type="checkbox"
                        checked={selectedRules.length === paginatedRules.length && paginatedRules.length > 0}
                        onChange={toggleSelectAll}
                        title="Seleccionar todas"
                      />
                    </th>
                    <th 
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('cliente_name')}
                      title="Click para ordenar"
                      className="position-relative"
                    >
                      <span className="d-inline-flex align-items-center gap-1">
                        Cliente
                        {getSortIcon('cliente_name')}
                      </span>
                    </th>
                    <th 
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('nombre')}
                      title="Click para ordenar"
                      className="position-relative"
                    >
                      <span className="d-inline-flex align-items-center gap-1">
                        Nombre
                        {getSortIcon('nombre')}
                      </span>
                    </th>
                    <th>Condiciones</th>
                    <th 
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('resultado')}
                      title="Click para ordenar"
                      className="position-relative"
                    >
                      <span className="d-inline-flex align-items-center gap-1">
                        Resultado
                        {getSortIcon('resultado')}
                      </span>
                    </th>
                    <th 
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('porcentaje_deducible')}
                      title="Click para ordenar"
                      className="position-relative"
                    >
                      <span className="d-inline-flex align-items-center gap-1">
                        % Deducible
                        {getSortIcon('porcentaje_deducible')}
                      </span>
                    </th>
                    <th 
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('fecha_aplicacion')}
                      title="Click para ordenar"
                      className="position-relative"
                    >
                      <span className="d-inline-flex align-items-center gap-1">
                        Vigencia
                        {getSortIcon('fecha_aplicacion')}
                      </span>
                    </th>
                    <th 
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('activo')}
                      title="Click para ordenar"
                      className="position-relative"
                    >
                      <span className="d-inline-flex align-items-center gap-1">
                        Estado
                        {getSortIcon('activo')}
                      </span>
                    </th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRules.map((rule) => {
                    const vigencia = getVigenciaStatus(rule)
                    return (
                      <tr 
                        key={rule.id}
                        className={selectedRules.includes(rule.id) ? 'table-primary' : ''}
                      >
                        <td>
                          <Form.Check
                            type="checkbox"
                            checked={selectedRules.includes(rule.id)}
                            onChange={() => toggleSelectRule(rule.id)}
                          />
                        </td>
                        <td>{rule.cliente_name || 'N/A'}</td>
                        <td><strong>{rule.nombre}</strong></td>
                        <td>
                          <small className="text-muted">
                            {rule.condiciones_legibles || 'Sin condiciones específicas'}
                          </small>
                        </td>
                        <td>
                          <Badge bg={
                            rule.resultado === 'deducible' ? 'success' :
                            rule.resultado === 'no_deducible' ? 'danger' :
                            'warning'
                          }>
                            {rule.resultado === 'deducible' ? 'Deducible' :
                             rule.resultado === 'no_deducible' ? 'No Deducible' :
                             'Parcial'}
                          </Badge>
                        </td>
                        <td>
                          {rule.resultado === 'parcial' && rule.porcentaje_deducible
                            ? `${rule.porcentaje_deducible}%`
                            : rule.resultado === 'deducible' ? '100%' : '0%'}
                        </td>
                        <td>
                          <div>
                            <small className="d-block">
                              <strong>Desde:</strong> {rule.fecha_aplicacion ? formatDateOnlyEsMX(rule.fecha_aplicacion, {}, 'N/A') : 'N/A'}
                            </small>
                            {rule.fecha_fin_vigencia ? (
                              <small className="d-block">
                                <strong>Hasta:</strong> {formatDateOnlyEsMX(rule.fecha_fin_vigencia, {}, 'N/A')}
                              </small>
                            ) : (
                              <small className="text-muted">Indefinida</small>
                            )}
                            <Badge bg={vigencia.badge} className="mt-1">
                              {vigencia.label}
                              {vigencia.days !== undefined && ` (${vigencia.days} día${vigencia.days !== 1 ? 's' : ''})`}
                            </Badge>
                          </div>
                        </td>
                        <td>
                          <Badge bg={rule.activo ? 'success' : 'secondary'}>
                            {rule.activo ? 'Activa' : 'Inactiva'}
                          </Badge>
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleOpenForm(rule)}
                              title="Editar"
                            >
                              ✏️
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDelete(rule)}
                              title="Eliminar"
                            >
                              🗑️
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
            </div>
            
            {/* Paginación */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                  <small className="text-muted">
                    Página {currentPage} de {totalPages}
                  </small>
                </div>
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  >
                    « Primera
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    ‹ Anterior
                  </Button>
                  <div className="d-flex align-items-center">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum
                      if (totalPages <= 5) {
                        pageNum = i + 1
                      } else if (currentPage <= 3) {
                        pageNum = i + 1
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i
                      } else {
                        pageNum = currentPage - 2 + i
                      }
                      
                      return (
                        <Button
                          key={pageNum}
                          variant={currentPage === pageNum ? 'primary' : 'outline-primary'}
                          size="sm"
                          onClick={() => setCurrentPage(pageNum)}
                          className="mx-1"
                        >
                          {pageNum}
                        </Button>
                      )
                    })}
                  </div>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Siguiente ›
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    Última »
                  </Button>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      ) : (
        <Alert variant="info">
          {searchValue || filterCliente || filterResultado || filterActivo !== 'all'
            ? 'No se encontraron reglas con los filtros aplicados.'
            : 'No hay reglas de deducibilidad configuradas. Haz clic en "Nueva Regla" para comenzar.'}
        </Alert>
      )}
      
      {/* Modal de formulario */}
      <Modal show={showForm} onHide={handleCloseForm} size="xl" fullscreen="lg-down">
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedRule ? 'Editar Regla de Deducibilidad' : 'Nueva Regla de Deducibilidad'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id="isGeneral"
                    label="General (aplica a todos los clientes)"
                    checked={formData.isGeneral}
                    onChange={(e) => {
                      const checked = e.target.checked
                      setFormData({
                        ...formData,
                        isGeneral: checked,
                        cliente: checked ? null : formData.cliente,
                        clientes: checked ? [] : formData.clientes
                      })
                    }}
                  />
                </Form.Group>
              </Col>
              {!formData.isGeneral && (
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Cliente(s) * (selección múltiple)</Form.Label>
                    {selectedRule ? (
                      <Form.Control
                        plaintext
                        readOnly
                        value={selectedRule.cliente_name || 'N/A'}
                      />
                    ) : (
                      <>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <Form.Text className="text-muted mb-0">
                            Se creará una copia de la regla para cada cliente seleccionado.
                          </Form.Text>
                          {clients && clients.length > 0 && (
                            <div className="d-flex gap-2">
                              <Button
                                type="button"
                                variant="outline-primary"
                                size="sm"
                                onClick={() => setFormData({
                                  ...formData,
                                  clientes: clients.map(c => c.id),
                                  cliente: clients[0]?.id || ''
                                })}
                              >
                                Seleccionar todos
                              </Button>
                              <Button
                                type="button"
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => setFormData({
                                  ...formData,
                                  clientes: [],
                                  cliente: ''
                                })}
                              >
                                Deseleccionar todos
                              </Button>
                            </div>
                          )}
                        </div>
                        <div
                          className="border rounded p-2"
                          style={{ maxHeight: '200px', overflowY: 'auto' }}
                        >
                          <Row>
                            {clients && (() => {
                              const mid = Math.ceil(clients.length / 2)
                              const left = clients.slice(0, mid)
                              const right = clients.slice(mid)
                              return (
                                <>
                                  <Col md={6}>
                                    {left.map((client) => (
                                      <Form.Check
                                        key={client.id}
                                        type="checkbox"
                                        id={`cliente-${client.id}`}
                                        label={getClientName(client)}
                                        checked={formData.clientes.includes(client.id)}
                                        onChange={(e) => {
                                          const checked = e.target.checked
                                          const newClientes = checked
                                            ? [...formData.clientes, client.id]
                                            : formData.clientes.filter(id => id !== client.id)
                                          setFormData({
                                            ...formData,
                                            clientes: newClientes,
                                            cliente: newClientes[0] || ''
                                          })
                                        }}
                                        className="mb-1"
                                      />
                                    ))}
                                  </Col>
                                  <Col md={6}>
                                    {right.map((client) => (
                                      <Form.Check
                                        key={client.id}
                                        type="checkbox"
                                        id={`cliente-${client.id}`}
                                        label={getClientName(client)}
                                        checked={formData.clientes.includes(client.id)}
                                        onChange={(e) => {
                                          const checked = e.target.checked
                                          const newClientes = checked
                                            ? [...formData.clientes, client.id]
                                            : formData.clientes.filter(id => id !== client.id)
                                          setFormData({
                                            ...formData,
                                            clientes: newClientes,
                                            cliente: newClientes[0] || ''
                                          })
                                        }}
                                        className="mb-1"
                                      />
                                    ))}
                                  </Col>
                                </>
                              )
                            })()}
                          </Row>
                        </div>
                      </>
                    )}
                  </Form.Group>
                </Col>
              )}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre de la Regla *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                    placeholder="Ej: Gastos de combustible deducibles"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Resultado *</Form.Label>
                  <Form.Select
                    value={formData.resultado}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        resultado: e.target.value,
                        porcentaje_deducible: e.target.value === 'parcial' ? formData.porcentaje_deducible : null
                      })
                    }}
                    required
                  >
                    <option value="deducible">Deducible (100%)</option>
                    <option value="no_deducible">No Deducible (0%)</option>
                    <option value="parcial">Parcial (% personalizado)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              {formData.resultado === 'parcial' && (
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Porcentaje Deducible *</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="number"
                        min="1"
                        max="100"
                        step="0.01"
                        value={formData.porcentaje_deducible || ''}
                        onChange={(e) => setFormData({ ...formData, porcentaje_deducible: parseFloat(e.target.value) || null })}
                        placeholder="Ej: 50"
                        required
                      />
                      <InputGroup.Text>%</InputGroup.Text>
                    </InputGroup>
                    <Form.Text className="text-muted">
                      Porcentaje del importe que es deducible (1-100)
                    </Form.Text>
                  </Form.Group>
                </Col>
              )}
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Fecha de Aplicación *</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.fecha_aplicacion}
                    onChange={(e) => setFormData({ ...formData, fecha_aplicacion: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Fecha Fin de Vigencia</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.fecha_fin_vigencia}
                    onChange={(e) => setFormData({ ...formData, fecha_fin_vigencia: e.target.value })}
                    min={formData.fecha_aplicacion}
                  />
                  <Form.Text className="text-muted">
                    Dejar vacío si la regla es indefinida
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Estado</Form.Label>
                  <Form.Check
                    type="switch"
                    label="Activa"
                    checked={formData.activo}
                    onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                  />
                </Form.Group>
              </Col>
            </Row>
            
            {/* Constructor de Condiciones Avanzado */}
            <hr />
            <h5 className="mb-3">Condiciones</h5>
            <p className="text-muted mb-3">
              <small>
                Define las condiciones que debe cumplir un concepto para que se aplique esta regla.
                Puedes crear condiciones simples o grupos anidados con operadores lógicos AND/OR.
              </small>
            </p>
            <ConditionBuilder
              value={formData.condiciones}
              onChange={handleConditionsChange}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseForm}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={createMutation.isLoading || updateMutation.isLoading}>
              {createMutation.isLoading || updateMutation.isLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Guardando...
                </>
              ) : (
                'Guardar'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      
      {/* Modal de confirmación de eliminación */}
      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Eliminación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Estás seguro de que deseas eliminar la regla &quot;{ruleToDelete?.nombre}&quot;?
          <br />
          <small className="text-muted">Esta acción no se puede deshacer.</small>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={confirmDelete}
            disabled={deleteMutation.isLoading}
          >
            {deleteMutation.isLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Eliminando...
              </>
            ) : (
              'Eliminar'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default DeductibilityRules
